import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, Chip,
  IconButton, Dialog, DialogActions, DialogContent, Grid,
  FormControlLabel, Checkbox
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';

// Codes couleur disponibles (les noms sont traduits dynamiquement via tags.colors)
const colorCodes = [
  '#ffcc80',
  '#90caf9',
  '#a5d6a7',
  '#f48fb1',
  '#ffab91',
  '#ce93d8',
  '#81c784',
  '#64b5f6',
  '#ba68c8',
  '#4fc3f7',
  '#e57373',
  '#fff176',
];

const AgendaTags = () => {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('');
  const [createBigboards, setCreateBigboards] = useState(false);
  const [tags, setTags] = useState([]);
  const [editTag, setEditTag] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper pour obtenir le nom traduit d'une couleur (réutilise les traductions de tags.colors)
  const getColorName = (code) => t(`tags.colors.${code}`, code);

  // Función para ordenar las etiquetas alfabéticamente
  const sortTags = (tagsToSort) => {
    return [...tagsToSort].sort((a, b) =>
      a.libelle.localeCompare(b.libelle, 'es', { sensitivity: 'base' })
    );
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/api/agenda_tags');
      setTags(sortTags(response.data));
    } catch (error) {
      console.error(t('agendaTags.fetchError'), error);
    }
  };

  const handleAddTag = async () => {
    if (tagName && tagColor) {
      try {
        const user = JSON.parse(localStorage.getItem('user'));

        const response = await api.post('/api/agenda_tags', {
          libelle: tagName,
          code_couleur: tagColor,
          create_bigboards: createBigboards,
          user_id: user.id
        });
        setTags(sortTags([...tags, response.data]));
        setTagName('');
        setTagColor('');
        setCreateBigboards(false);
      } catch (error) {
        console.error(t('agendaTags.addError'), error);
      }
    }
  };

  const handleEditTag = (tag) => {
    setEditTag(tag);
    setTagName(tag.libelle);
    setTagColor(tag.code_couleur);
    setCreateBigboards(false);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/api/agenda_tags/${editTag.id}`, {
        libelle: tagName,
        code_couleur: tagColor
      });
      const updatedTags = tags.map((tag) =>
        tag.id === editTag.id ? { ...tag, libelle: tagName, code_couleur: tagColor } : tag
      );
      setTags(sortTags(updatedTags));
      setTagName('');
      setTagColor('');
      setEditTag(null);
    } catch (error) {
      console.error(t('agendaTags.updateError'), error);
    }
  };

  const handleDeleteTag = async (tag) => {
    try {
      await api.delete(`/api/agenda_tags/${tag.id}`);
      const updatedTags = tags.filter((item) => item.id !== tag.id);
      setTags(sortTags(updatedTags));
      setConfirmDelete(null);
    } catch (error) {
      console.error(t('agendaTags.deleteError'), error);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t('agendaTags.title')}
      </Typography>

      <Grid container spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('agendaTags.tagName')}
            variant="outlined"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Select
            value={tagColor}
            onChange={(e) => setTagColor(e.target.value)}
            displayEmpty
            variant="outlined"
            fullWidth
          >
            <MenuItem value="" disabled>
              {t('agendaTags.selectColor')}
            </MenuItem>
            {colorCodes.map((code) => (
              <MenuItem key={code} value={code}>
                {getColorName(code)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {!editTag && (
          <Grid item xs={12} sm={2} sx={{ display: 'none' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createBigboards}
                  onChange={(e) => setCreateBigboards(e.target.checked)}
                />
              }
              label={t('agendaTags.createRankings')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={2}>
          <Button
            variant="contained"
            onClick={editTag ? handleSaveEdit : handleAddTag}
            sx={{ backgroundColor: '#ff9800', color: 'white', height: '100%', width: '150px' }}
          >
            {editTag ? t('agendaTags.saveChanges') : t('agendaTags.addTag')}
          </Button>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('agendaTags.existingTags')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tags.map((tag) => (
            <Box key={tag.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={tag.libelle}
                sx={{ backgroundColor: tag.code_couleur, color: 'white' }}
              />
              <IconButton onClick={() => handleEditTag(tag)} sx={{ color: '#ff9800' }}>
                <Edit />
              </IconButton>
              <IconButton onClick={() => setConfirmDelete(tag)} sx={{ color: 'red' }}>
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <Typography variant="h6">
            {t('agendaTags.confirmDelete', { name: confirmDelete?.libelle })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: 'grey' }}>
            {t('agendaTags.cancel')}
          </Button>
          <Button onClick={() => handleDeleteTag(confirmDelete)} sx={{ color: 'red' }}>
            {t('agendaTags.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaTags;
